import type { MouseEvent } from "react";

import { useUserpilot } from "@platform/hooks/useUserpilot";

import useDialogStore from "@store/useDialogStore";
import usePlatformStore from "@store/usePlatformStore";
import { useCreateCompany } from "./hooks/useCreateCompany";
import { useCreateDeal } from "./hooks/useCreateDeal";
import { getCompanyTaggingOption, getDealTaggingOption } from "@utils/tags.util";
import { Deal } from "@app-types/common.type";
import { CreateCompanyRequest, CreateDealRequest } from "@app-types/requests.type";
import { Source } from "@enums/common.enum";
import { RelationObjectType } from "@enums/relations.enum";
import { ICompany } from "@interfaces/company.interface";
import { ICompanyTag, IDealTag } from "@interfaces/tag.interface";
import { AddNewCompanyDialog } from "./Company";
import { AddNewDealDialog } from "./Deal";
import { Button } from "@ui/Button";
import PlusIcon from "@assets/icons/plus.svg";

import "./NewEntityButtons.css";

interface Props {
  contentType: RelationObjectType;
  // Tag the freshly created entity to the current item (email/meeting); owner supplies the handler.
  onEntityCreated?: (option: ICompanyTag | IDealTag) => void;
}

export function NewEntityButtons({ contentType, onEntityCreated }: Props) {
  const { track } = useUserpilot();
  const { isOutlook } = usePlatformStore();
  const { openDialog, closeDialog } = useDialogStore();
  const { mutateAsync: createCompanyMutate } = useCreateCompany(contentType, closeDialog);
  const { mutateAsync: createDealMutate } = useCreateDeal(contentType, closeDialog);

  const sourceItem = contentType === RelationObjectType.EMAIL_THREAD ? "emails" : "meetings";
  const sourcePlatform = isOutlook() ? Source.OUTLOOK_ADDIN : Source.CHROME_EXTENSION;

  const buttonClassName =
    "flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80 focus:dark:bg-gray-600";

  const handleCreateCompany = async (data: CreateCompanyRequest): Promise<ICompany> => {
    const company = await createCompanyMutate(data);
    if (company) onEntityCreated?.(getCompanyTaggingOption(company));
    return company;
  };

  const handleCreateDeal = async (data: CreateDealRequest): Promise<Deal> => {
    const deal = await createDealMutate(data);
    if (deal) onEntityCreated?.(getDealTaggingOption(deal));
    return deal;
  };

  const handleOpenNewCompanyDialog = (event: MouseEvent<HTMLButtonElement>) => {
    // Blur first so the dialog restores focus to <body>, not this button (avoids a stuck focus ring on close).
    event.currentTarget.blur();
    openDialog(
      <AddNewCompanyDialog
        query=""
        onSave={handleCreateCompany}
        sourceItem={sourceItem}
        sourcePlatform={sourcePlatform}
      />,
      { disableOutsideClick: true }
    );
    track(`Click: Open new company dialog (${sourceItem})`);
  };

  const handleOpenNewDealDialog = (event: MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    openDialog(
      <AddNewDealDialog query="" onSave={handleCreateDeal} sourceItem={sourceItem} sourcePlatform={sourcePlatform} />,
      { disableOutsideClick: true }
    );
    track(`Click: Open new deal dialog (${sourceItem})`);
  };

  return (
    <div className="new-entity-buttons">
      <div className="new-entity-buttons__row">
        <Button
          buttonType="SECONDARY"
          PrefixIcon={PlusIcon}
          className={buttonClassName}
          onClick={handleOpenNewCompanyDialog}
        >
          New company
        </Button>
        <Button
          buttonType="SECONDARY"
          PrefixIcon={PlusIcon}
          className={buttonClassName}
          onClick={handleOpenNewDealDialog}
        >
          New deal
        </Button>
      </div>
    </div>
  );
}
